import { useMemo, useState } from "react";
import { Check, Search, UserMinus } from "lucide-react";
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useGetUsersQuery } from "@/services/users/usersApi";

/**
 * The body of an assignee dropdown: search, the filtered people list, and an
 * optional Unassign row. Shared by the table/detail assignee picker and the
 * toolbar's bulk-assign menu, which previously kept two copies of this list
 * that had drifted apart (one had avatars and a current-assignee check mark,
 * the other was plain text).
 *
 * Presentation only — closing the menu and calling the assign/unassign
 * endpoint stays the caller's job, done inside the `onSelect`/`onUnassign`
 * callbacks it passes in.
 */
export function AssigneeMenuContent({
  assignees = [],
  selectedGuid = null,
  onSelect,
  onUnassign,
  label,
}) {
  const [query, setQuery] = useState("");
  // The people list is fetched lazily (see useAssignees), so an empty list can
  // mean "still loading" or "could not load", not only "no match". Same cached
  // request the caller already made, so this adds no traffic.
  const { isLoading, isError } = useGetUsersQuery();

  const filtered = useMemo(() => {
    const people = (assignees ?? []).filter(Boolean);
    const term = query.trim().toLowerCase();
    if (!term) return people;
    return people.filter((user) => user.name?.toLowerCase().includes(term));
  }, [assignees, query]);

  // The menu's own keyboard handling (arrow-key focus, type-to-jump) would
  // otherwise steal every keystroke typed into the search box.
  const keepInputKeys = (event) => {
    if (event.key !== "Escape") event.stopPropagation();
  };

  return (
    <DropdownMenuGroup>
      {label && <DropdownMenuLabel>{label}</DropdownMenuLabel>}
      <div className="px-1.5 pt-1 pb-1.5">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={keepInputKeys}
            placeholder="Search people…"
            aria-label="Search people"
            autoFocus
            className="h-8 pl-8 text-xs"
          />
        </div>
      </div>
      <DropdownMenuSeparator />

      {/* A fixed-height list: the menu keeps its size while the search narrows the
          people shown, instead of shrinking and jumping under the pointer. */}
      <div className="h-56 overflow-y-auto overscroll-contain">
        {filtered.length === 0 && (
          <div className="px-2 py-3 text-center text-xs text-muted-foreground">
            {(assignees ?? []).length > 0 || (!isLoading && !isError)
              ? "No matches"
              : isLoading
                ? "Loading people…"
                : "Couldn't load people"}
          </div>
        )}
        {filtered.map((user) => {
          const isCurrent = user.guid === selectedGuid;
          return (
            <DropdownMenuItem
              key={user.guid}
              onClick={(event) => {
                event.stopPropagation();
                onSelect?.(user.guid);
              }}
              className="gap-2"
            >
              <UserAvatar user={user} size="sm" />
              <span className="min-w-0 flex-1 truncate">{user.name}</span>
              {isCurrent && <Check className="size-3.5 shrink-0 text-primary" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </div>

      {onUnassign && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation();
              onUnassign();
            }}
            className="gap-2"
          >
            <UserMinus className="size-4" aria-hidden="true" />
            Unassign
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuGroup>
  );
}
